import { Link, useNavigate } from "@tanstack/react-router";
import { BookOpen, Check, ChevronsUpDown, FolderKanban, LifeBuoy, Plus, Settings2, Sparkles, Users } from "lucide-react";
import { useState } from "react";
import {
  Badge,
  describeStatus,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  GradeBadge,
  Meter,
  Money,
  Skeleton,
  StatusDot,
  askGuv,
} from "@/design";
import { useApprovals, useMe, useProjectStatus, useProjects, useWorkspaceSpend } from "@/lib/api";
import { cn } from "@/lib/cn";
import { docsUrl } from "@/lib/config";
import { moneyAmount } from "@/lib/format";
import { useProjectSlug, useWorkspaceSlug } from "./context";
import { Logo } from "./Logo";
import { NewWorkspaceDialog } from "./NewWorkspaceDialog";
import { projectNav } from "./nav";

const navItem = cn(
  "group flex h-8 items-center gap-2.5 rounded-md px-2.5 text-base font-medium text-muted transition-colors",
  "hover:bg-surface-3/60 hover:text-text [&_svg]:size-4 [&_svg]:shrink-0 [&>svg]:text-subtle hover:[&>svg]:text-muted",
  "data-[status=active]:bg-surface data-[status=active]:text-text data-[status=active]:shadow-xs data-[status=active]:ring-1 data-[status=active]:ring-border data-[status=active]:[&>svg]:text-accent-text",
);

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const workspace = useWorkspaceSlug();
  const project = useProjectSlug();
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center px-4">
        <Link to="/$workspace" params={{ workspace }} onClick={onNavigate} className="rounded-md">
          <Logo />
        </Link>
      </div>
      <div className="px-3">
        <WorkspaceSwitcher />
      </div>
      <nav className="scrollbar-thin mt-4 flex-1 overflow-y-auto px-3 pb-4" aria-label="Main">
        <Link to="/$workspace" params={{ workspace }} activeOptions={{ exact: true }} className={navItem} onClick={onNavigate}>
          <FolderKanban /> Projects
        </Link>
        {!project && (
          <>
            <Link to="/$workspace/settings" params={{ workspace }} hash="members" activeOptions={{ includeHash: true }} className={navItem} onClick={onNavigate}>
              <Users /> Members
            </Link>
            <Link to="/$workspace/settings" params={{ workspace }} activeOptions={{ includeHash: true }} className={navItem} onClick={onNavigate}>
              <Settings2 /> Workspace settings
            </Link>
          </>
        )}
        {project ? <ProjectNav onNavigate={onNavigate} /> : <ProjectList onNavigate={onNavigate} />}
      </nav>
      <SidebarFooter />
    </div>
  );
}

function WorkspaceSwitcher() {
  const workspace = useWorkspaceSlug();
  const navigate = useNavigate();
  const me = useMe();
  const current = me.data?.workspaces.find((w) => w.slug === workspace);
  const [creating, setCreating] = useState(false);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex w-full items-center gap-2.5 rounded-lg border border-border bg-surface px-2.5 py-2 text-left shadow-xs transition-colors hover:border-border-strong">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-3 font-display text-sm font-semibold text-text">
          {(current?.name ?? workspace).charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-text">{current?.name ?? <Skeleton className="h-3.5 w-28" />}</span>
          <span className="block text-xs text-subtle">{current?.plan ? `${current.plan.charAt(0).toUpperCase()}${current.plan.slice(1)} plan` : "…"}</span>
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-subtle" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60">
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        {me.data?.workspaces.map((w) => (
          <DropdownMenuItem key={w.id} onSelect={() => navigate({ to: "/$workspace", params: { workspace: w.slug } })}>
            <span className="flex size-5 items-center justify-center rounded bg-surface-3 text-2xs font-semibold">{w.name.charAt(0)}</span>
            <span className="flex-1 truncate">{w.name}</span>
            {w.slug === workspace && <Check className="text-accent-text!" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Settings2 />} onSelect={() => navigate({ to: "/$workspace/settings", params: { workspace } })}>
          Workspace settings
        </DropdownMenuItem>
        <DropdownMenuItem icon={<Plus />} onSelect={() => setCreating(true)}>
          New workspace
        </DropdownMenuItem>
      </DropdownMenuContent>
      <NewWorkspaceDialog open={creating} onOpenChange={setCreating} />
    </DropdownMenu>
  );
}

function ProjectList({ onNavigate }: { onNavigate?: () => void }) {
  const workspace = useWorkspaceSlug();
  const projects = useProjects(workspace);
  return (
    <div className="mt-5">
      <div className="mb-1.5 flex items-center justify-between px-2.5">
        <span className="text-2xs font-semibold uppercase tracking-[0.08em] text-subtle">Your projects</span>
        <Link to="/$workspace/new" params={{ workspace }} className="rounded p-0.5 text-subtle hover:bg-surface-3 hover:text-text" aria-label="New project" onClick={onNavigate}>
          <Plus className="size-3.5" />
        </Link>
      </div>
      {projects.isPending ? (
        <div className="space-y-2 px-2.5 pt-1">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      ) : (
        projects.data?.data.map((p) => (
          <Link key={p.id} to="/$workspace/$project" params={{ workspace, project: p.slug }} className={navItem} onClick={onNavigate}>
            <ProjectHealthDot project={p.slug} />
            <span className="flex-1 truncate">{p.name}</span>
            <GradeBadge grade={p.safety?.grade} size="sm" />
          </Link>
        ))
      )}
    </div>
  );
}

function ProjectHealthDot({ project }: { project: string }) {
  const s = useProjectStatus(project);
  const prod = s.data?.environments.find((e) => e.name === "production") ?? s.data?.environments[0];
  const info = describeStatus("health", prod?.status);
  return <StatusDot tone={s.isPending ? "neutral" : info.tone} pulse={info.pulse} size="sm" />;
}

function ProjectNav({ onNavigate }: { onNavigate?: () => void }) {
  const workspace = useWorkspaceSlug();
  const project = useProjectSlug();
  const navigate = useNavigate();
  const status = useProjectStatus(project);
  const projects = useProjects(workspace);
  const approvals = useApprovals(workspace, { status: "pending", project });
  const p = status.data?.project;
  const pending = approvals.data?.data.length ?? 0;

  return (
    <div className="mt-5">
      <DropdownMenu>
        <DropdownMenuTrigger className="mb-2 flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left hover:bg-surface-3/60">
          <span className="min-w-0 flex-1">
            <span className="block text-2xs font-semibold uppercase tracking-[0.08em] text-subtle">Project</span>
            <span className="block truncate text-md font-semibold text-text">{p?.name ?? project}</span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-subtle" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          {projects.data?.data.map((x) => (
            <DropdownMenuItem key={x.id} onSelect={() => navigate({ to: "/$workspace/$project", params: { workspace, project: x.slug } })}>
              <ProjectHealthDot project={x.slug} />
              <span className="flex-1 truncate">{x.name}</span>
              {x.slug === project && <Check className="text-accent-text!" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem icon={<Plus />} onSelect={() => navigate({ to: "/$workspace/new", params: { workspace } })}>
            New project
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="space-y-0.5">
        {projectNav.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            params={{ workspace, project } as never}
            search={((s: Record<string, unknown>) => ({ env: s.env })) as never}
            activeOptions={{ exact: !!item.exact, includeSearch: false }}
            className={navItem}
            onClick={onNavigate}
          >
            {item.icon}
            <span className="flex-1 truncate">{item.label}</span>
            {item.key === "safety" && status.data?.safety_grade && <GradeBadge grade={status.data.safety_grade} size="sm" />}
            {item.key === "spend" && status.data?.spend?.month_to_date && (
              <span className="text-xs font-normal text-subtle">
                <Money value={status.data.spend.month_to_date} />
              </span>
            )}
            {item.key === "agents" && pending > 0 && (
              <Badge tone="warn" size="sm" className="tabular">
                {pending}
              </Badge>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

function SidebarFooter() {
  const workspace = useWorkspaceSlug();
  const spend = useWorkspaceSpend(workspace);
  const cap = spend.data?.caps?.[0];
  const mtd = moneyAmount(spend.data?.month_to_date);
  const capAmt = moneyAmount(cap?.amount);
  return (
    <div className="space-y-3 border-t border-border p-3">
      {cap && (
        <div className="rounded-lg border border-border bg-surface p-3 shadow-xs">
          <div className="flex items-baseline justify-between text-xs">
            <span className="font-medium text-muted">This month</span>
            <span className="text-subtle">
              <Money value={capAmt} whole /> cap
            </span>
          </div>
          <div className="mt-1 font-display text-xl font-semibold tabular text-text">
            <Money value={mtd} />
          </div>
          <Meter className="mt-2" size="sm" value={mtd} max={capAmt} forecast={moneyAmount(spend.data?.forecast_month_end)} label="Workspace spend against cap" />
          {cap.state !== "ok" && (
            <p className="mt-2 text-xs text-warn-text">
              Heading for <Money value={spend.data?.forecast_month_end} />. Non-production apps sleep at the cap.
            </p>
          )}
        </div>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => askGuv({ question: "" })}
          className="flex h-8 flex-1 items-center gap-2 rounded-md px-2.5 text-sm font-medium text-ai-text hover:bg-ai-soft"
        >
          <Sparkles className="size-4" /> Ask Guv
        </button>
        <a href={docsUrl("/docs")} target="_blank" rel="noreferrer" className="flex size-8 items-center justify-center rounded-md text-subtle hover:bg-surface-3 hover:text-text" aria-label="Documentation">
          <BookOpen className="size-4" />
        </a>
        <a href={docsUrl("/support")} target="_blank" rel="noreferrer" className="flex size-8 items-center justify-center rounded-md text-subtle hover:bg-surface-3 hover:text-text" aria-label="Help and support">
          <LifeBuoy className="size-4" />
        </a>
      </div>
    </div>
  );
}
